/**
 * W25 (FR-60, D76) — **the folders in the host's left menu**, under its
 * Documents item. `useProDocFolderMenu` gives the host the tree for that item's
 * `panel` (platform FR-24c: `@fiveox/shell`'s `NavItem.panel`) and the folder
 * dialogs to mount once beside its shell.
 *
 * - **The tree** is the register's own (`FolderTree`, `variant="menu"`): *Every
 *   workspace*, *Shared with me*, each workspace the reader sees with its
 *   folders at any depth, branches read as they open, and the same "…" actions
 *   by permission — new folder, rename, move, share, archive.
 * - **Its state lives here, in the host** — the branches read and the places
 *   open — because the shell mounts the panel **twice** while its drawer is open
 *   (the drawer, and the rail the drawer covers) and unmounts it while the group
 *   is closed. Both copies are one tree, each branch is read once, and nothing in
 *   the tree carries a fixed `id`.
 * - **Choosing a place** hands the host the register's question for it
 *   (`onChoose`): the host puts it in its address, so back and forward work.
 *   The place the host is standing in (`query`) is highlighted, and a folder
 *   reached some other way — a link, a saved view, back and forward after a
 *   branch was closed — is revealed: its workspace and the folders above it open.
 * - **The dialogs** are `dialogs`, mounted once by the host, outside the menu.
 *   After one changes something the tree reads again, `revision` bumps for the
 *   host's register (`ProDocLibrary`'s `folderRevision`), a new folder is
 *   chosen, and an archived folder the host was standing in gives way to its
 *   parent — as the register's own panel did.
 *
 * A person without `folder:can_view` gets no panel (`null`), so the item stays
 * a plain link; while the answer is on its way the panel is the tree's skeleton.
 *
 * Package rules (ADR-0010): the host's fetcher, org, sharing and address come in
 * as options; nothing here knows a router, a session or the shell.
 */
import { useToast } from '@fiveox/ui'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { FolderDialogs, folderDoneText } from './folder-dialogs'
import { FolderTree, useFolderTreeState, type FolderAction, type FolderPlace, type FolderTreeState } from './folder-tree'
import { useLoad, useProDocAccess, useProDocClient, useWorkspaces, type HostProps } from './hooks'
import { DEFAULT_QUERY, placeChanges, sharedChanges, type LibraryQuery } from './library'
import { FOLDER, type Folder } from './prodoc-client'
import { ShareDialog, type ShareTarget, type SharingHostProps } from './share-dialog'

export interface ProDocFolderMenuOptions extends HostProps, SharingHostProps {
  /**
   * Where the host's register stands — its address, read with
   * `libraryQueryFromParams` — or `null` on any other screen, where nothing is
   * highlighted and a choice starts from the register's defaults.
   */
  query: LibraryQuery | null
  /** A place was chosen: the register's question for it, every other filter kept. The host opens its register there, as a new step in its history. */
  onChoose: (next: LibraryQuery) => void
}

export interface ProDocFolderMenu {
  /** The tree, for the Documents item's `panel` — mount it as often as the shell does. `null` for a person who may not see folders. */
  panel: ReactNode
  /** The folder dialogs and the share dialog: mount **once**, outside the menu. */
  dialogs: ReactNode
  /** Bumps after each change the dialogs made — pass it to the register as `folderRevision`. */
  revision: number
  /** Read every open branch again — the register's `onFolderChange`, after a bulk move or archive. */
  refresh: () => void
}

const NO_TARGET: ShareTarget = { documents: [], folders: [] }

export function useProDocFolderMenu(options: ProDocFolderMenuOptions): ProDocFolderMenu {
  const { query, onChoose, userId, listMembers, requestStepUp, shareLinkBase } = options
  const client = useProDocClient(options)
  const access = useProDocAccess(client)
  const { toast } = useToast()
  const workspaces = useWorkspaces(client)
  const service = useLoad(() => client.documents.options(), [client])
  const app = service.data?.app ?? 'prodoc'
  const canFolders = access.can(FOLDER, 'can_view')
  // `GET /folders?shared=true` is behind `folder:can_view`, as every folder read is.
  const sharedFolders = useLoad(() => (canFolders ? client.folders.shared() : Promise.resolve([] as Folder[])), [client, canFolders])
  const visible = access.answer?.visible_workspace_ids
  const seen = useMemo(() => workspaces.list.filter((w) => w.status !== 'archived' && (visible == null || visible.includes(w.id))), [workspaces.list, visible])

  // In the shared view the place is a shared folder: it is highlighted under *Shared with me*, not in its workspace.
  const selected: FolderPlace | null = query ? (query.shared ? { workspace: null, folder: null } : { workspace: query.workspace, folder: query.folder }) : null
  const state = useFolderTreeState(client, { selected, workspaces: seen })

  // A folder reached some other way is revealed: walk up through what is already read, or ask its breadcrumb.
  const latest = useRef(state)
  latest.current = state
  const target = selected?.folder ?? null
  const targetWorkspace = selected?.workspace ?? null
  useEffect(() => {
    if (!target || !targetWorkspace || !canFolders) return
    const ancestors: string[] = []
    let f = latest.current.find(target)
    while (f?.parent_id) {
      ancestors.unshift(f.parent_id)
      f = latest.current.find(f.parent_id)
    }
    if (f) {
      reveal(latest.current, targetWorkspace, ancestors)
      return
    }
    let cancelled = false
    client.folders
      .get(target)
      .then((detail) => {
        if (!cancelled) reveal(latest.current, targetWorkspace, detail.breadcrumb.map((c) => c.id).filter((id) => id !== target))
      })
      // A folder that cannot be read is said above the table (the breadcrumb); the menu just stays as it is.
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [client, target, targetWorkspace, canFolders])

  const base = query ?? DEFAULT_QUERY
  const choose = (changes: Partial<LibraryQuery>) => onChoose({ ...base, ...changes, page: 1 })
  const choosePlace = (place: FolderPlace) => choose(placeChanges(base, place))
  const chooseShared = (folder: Folder | null) => choose(sharedChanges(folder))

  const [action, setAction] = useState<FolderAction | null>(null)
  const [sharing, setSharing] = useState<ShareTarget | null>(null)
  const [revision, setRevision] = useState(0)
  const onAction = useCallback(
    (a: FolderAction) => (a.kind === 'share' ? setSharing({ documents: [], folders: [{ id: a.folder.id, name: a.folder.name }] }) : setAction(a)),
    [],
  )
  const { refresh } = state

  const done = (folder: Folder, kind: FolderAction['kind']) => {
    const was = action
    setAction(null)
    refresh()
    setRevision((n) => n + 1)
    toast(folderDoneText(folder, kind))
    if (kind === 'create') choosePlace({ workspace: folder.workspace_id, folder: folder.id })
    // An archived folder the register was standing in: step up to its parent.
    if (kind === 'archive' && was?.kind === 'archive' && query && !query.shared && query.folder === was.folder.id) {
      choosePlace({ workspace: was.folder.workspace_id, folder: was.folder.parent_id })
    }
  }

  const panel =
    access.loading || canFolders ? (
      <FolderTree
        variant="menu"
        state={state}
        client={client}
        workspaces={access.loading ? [] : seen}
        workspacesLoading={access.loading || workspaces.loading}
        workspacesError={workspaces.error}
        onRetryWorkspaces={() => void workspaces.reload()}
        app={app}
        selected={selected}
        onSelect={choosePlace}
        can={access.can}
        onAction={onAction}
        shared={{
          folders: sharedFolders.data,
          loading: sharedFolders.loading && !sharedFolders.data,
          error: sharedFolders.error,
          active: Boolean(query?.shared),
          folder: query?.shared ? query.folder : null,
          onSelect: chooseShared,
        }}
      />
    ) : null

  const dialogs = (
    <>
      <FolderDialogs client={client} action={action} workspaceName={(id) => workspaces.nameOf(id)} onClose={() => setAction(null)} onDone={done} />
      <ShareDialog
        client={client}
        open={sharing !== null}
        target={sharing ?? NO_TARGET}
        can={access.can}
        userId={userId}
        listMembers={listMembers}
        requestStepUp={requestStepUp}
        shareLinkBase={shareLinkBase}
        onClose={() => setSharing(null)}
      />
    </>
  )

  return { panel, dialogs, revision, refresh }
}

/** Open the workspace and every folder above the chosen one, reading each of their branches. */
function reveal(tree: FolderTreeState, workspaceId: string, ancestors: string[]) {
  tree.expand([workspaceId, ...ancestors])
  for (const id of ancestors) tree.open(workspaceId, id)
}
